import { useEffect, useRef, useState } from 'react';

import { duracion } from '@haus/core';

import { Aviso, Pantalla } from '../componentes/estructura.js';
import { useCamara } from '../captura/useCamara.js';
import { useGps } from '../captura/useGps.js';
import { useGrabacion } from '../captura/useGrabacion.js';
import {
    guiaEstancia,
    leerLimpieza,
    nombreEstancia,
    type EstanciaLocal,
    type LimpiezaLocal,
} from '../datos/db.js';
import { marcarEstancia } from '../datos/limpiezas.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 05 · Grabando con guía.
 *
 * LO QUE PASA AL PARAR ES LO IMPORTANTE: el clip va a IndexedDB y **entra en
 * la cola inmediatamente**, no al final de la limpieza. Mientras la empleada
 * camina a la estancia siguiente y la graba, ese clip ya está subiendo. En un
 * iPhone, donde no hay subida en segundo plano, esa ventana es la única que
 * hay (decisión D3).
 *
 * El guion de cada estancia sale de la plantilla que configura oficina y
 * viene en los dos idiomas dentro del día cacheado, así que se ve sin red.
 */
export function PantallaGrabar({ limpiezaId, estanciaId }: { limpiezaId: string; estanciaId: string }) {
    const { t, idioma, ajustes, cola, atras, capacidades } = useApp();
    const camara = useCamara(ajustes.calidad, true);
    const { pedir } = useGps();
    const video = useRef<HTMLVideoElement>(null);
    const [l, setL] = useState<LimpiezaLocal | null>(null);
    const [linternaOn, setLinternaOn] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [aviso, setAviso] = useState<string | null>(null);

    const estancia: EstanciaLocal | undefined = l?.estancias.find((e) => e.id === estanciaId);
    const grabacion = useGrabacion(camara.stream, estancia?.max_video_s ?? 90);

    useEffect(() => {
        void leerLimpieza(limpiezaId).then(setL);
    }, [limpiezaId]);

    useEffect(() => {
        void camara.encender();
        // encender es estable; incluirla dispararía un ciclo de renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (camara.stream !== null && video.current !== null) {
            video.current.srcObject = camara.stream;
        }
    }, [camara.stream]);

    // Aviso al acercarse al máximo: el handoff pide avisar a los 80 s de 90.
    useEffect(() => {
        const max = estancia?.max_video_s ?? 90;
        if (grabacion.estado === 'grabando' && max - grabacion.segundos <= 10 && max - grabacion.segundos > 0) {
            setAviso(t('grabar.aviso_largo', { segundos: max - grabacion.segundos }));
        } else if (grabacion.estado !== 'grabando') {
            setAviso(null);
        }
    }, [grabacion.estado, grabacion.segundos, estancia?.max_video_s, t]);

    async function parar(): Promise<void> {
        if (l === null || estancia === undefined) {
            return;
        }

        const minimo = estancia.min_video_s;
        if (grabacion.segundos < minimo) {
            // No se descarta lo grabado: se avisa y se sigue grabando. Perder
            // veinte segundos de trabajo por un toque de más sería peor.
            setAviso(t('grabar.corto', { segundos: minimo }));

            return;
        }

        setGuardando(true);
        const ms = grabacion.segundos * 1000;
        const blob = await grabacion.parar();

        if (blob === null) {
            setGuardando(false);
            setAviso(t('error.inesperado'));

            return;
        }

        const gps = await pedir();

        // Lo que la cámara ha dado DE VERDAD, no lo que se le pidió.
        //
        // Estas dos columnas existen desde 0005 y nadie las rellenaba nunca:
        // iban a `null` aquí y en la pantalla de fotos. Y son justo las que
        // hacen comprobable el ajuste de calidad de la oficina: `calidad` dice
        // lo que se pidió —480p— y esto dice lo que se grabó. Un móvil cuya
        // cámara no puede bajar de 720 declararía 480p igual, y con las dos
        // columnas se ve la diferencia en vez de suponerla.
        //
        // `getSettings()` es lo que negoció el track, no la constraint.
        const pista = camara.stream?.getVideoTracks()[0]?.getSettings();

        await cola.anadir({
            cleaning_id: l.id,
            cleaning_room_id: estancia.id,
            room_punto_id: null,
            apartamento: l.apartamento,
            estancia: nombreEstancia(estancia, idioma),
            tipo: 'video',
            blob,
            capturado_at: new Date().toISOString(),
            duracion_ms: ms,
            ancho: pista?.width ?? null,
            alto: pista?.height ?? null,
            calidad: ajustes.calidad,
            gps,
        });

        await marcarEstancia(l.id, estancia.id, { estado: 'hecha', video_ms: ms });
        camara.apagar();
        // Atrás y no "ir al recorrido": grabar es un paso ENCIMA del recorrido,
        // no otro sitio. Apilando, el botón atrás del sistema devolvía a la
        // cámara y volvía a encenderla, que es lo último que quiere alguien que
        // acaba de terminar una estancia.
        atras();
    }

    if (estancia === undefined || l === null) {
        return <Pantalla fondo="oscuro" />;
    }

    const nombre = nombreEstancia(estancia, idioma);
    const guia = guiaEstancia(estancia, idioma);
    const posicion = l.estancias.filter((e) => e.orden <= estancia.orden).length;

    if (!grabacion.soportado || camara.estado === 'sin_camara' || camara.estado === 'denegada') {
        // Sin cámara no hay producto, así que la ayuda es específica del
        // sistema: "Ajustes → Safari → Cámara" no vale en Android.
        return (
            <Pantalla>
                <div style={{ padding: 'var(--e22)', display: 'flex', flexDirection: 'column', gap: 'var(--e14)' }}>
                    <h1 className={css.titulo}>{t('grabar.sin_camara')}</h1>
                    <p className={css.parrafo}>
                        {capacidades.esIos ? t('grabar.sin_camara_ios') : t('grabar.sin_camara_android')}
                    </p>
                    {capacidades.esIos && !capacidades.instalada && (
                        <Aviso tono="info">{t('grabar.sin_camara_pwa')}</Aviso>
                    )}
                    <button className={css.parrafo} onClick={atras}>
                        {t('comun.atras')}
                    </button>
                </div>
            </Pantalla>
        );
    }

    return (
        <Pantalla fondo="oscuro">
            <div className={css.visor}>
                <video ref={video} className={css.video} playsInline muted autoPlay />

                <div className={css.chipsFlotantes}>
                    <span className={css.chipOscuro}>
                        {grabacion.estado === 'grabando' && <span className={css.puntoRec} />}
                        <span className="mono">{duracion(grabacion.segundos * 1000)}</span>
                    </span>
                    <span className={css.chipOscuro}>
                        {t('grabar.contexto', {
                            estancia: nombre,
                            n: posicion,
                            total: l.estancias.length,
                        })}
                    </span>
                </div>

                {guia !== null && guia !== '' && (
                    <div className={css.guia}>
                        <span className={css.guiaTitulo}>{t('grabar.guia_titulo')}</span>
                        <span className={css.guiaTexto}>{guia}</span>
                    </div>
                )}

                {aviso !== null && (
                    <div style={{ position: 'absolute', top: 70, left: 14, right: 14 }}>
                        <Aviso>{aviso}</Aviso>
                    </div>
                )}
            </div>

            <div className={css.controles}>
                <button
                    className={css.botonRedondo}
                    aria-label={t('grabar.repetir')}
                    disabled={grabacion.estado !== 'grabando'}
                    onClick={() => grabacion.descartar()}
                >
                    ⤾
                </button>

                <button
                    className={css.botonRec}
                    aria-label={grabacion.estado === 'grabando' ? t('grabar.parar') : t('grabar.empezar')}
                    disabled={guardando || camara.estado !== 'lista'}
                    onClick={() => (grabacion.estado === 'grabando' ? void parar() : grabacion.empezar())}
                >
                    <span
                        className={`${css.botonRecInterior} ${
                            grabacion.estado === 'grabando' ? css.botonRecGrabando : ''
                        }`}
                    />
                </button>

                <button
                    className={`${css.botonRedondo} ${linternaOn ? css.botonRedondoActivo : ''}`}
                    aria-label={t('grabar.linterna')}
                    disabled={!camara.tieneLinterna}
                    onClick={() => {
                        void camara.linterna(!linternaOn).then((ok) => {
                            if (ok) {
                                setLinternaOn(!linternaOn);
                            }
                        });
                    }}
                >
                    ⚡
                </button>
            </div>
        </Pantalla>
    );
}
